/**
 * WhiteLabelProtectedRoute — Bloqueia acesso ao /wl para quem não é operador
 * do white label nem root_admin observador.
 *
 * CHANGELOG:
 *   v1.1.0 — Em caso de falha técnica no RPC (state.error), exibe tela de erro
 *            controlada em vez de redirecionar para /access-denied ou /. Isso
 *            evita loop /wl ⇄ /onboarding quando o RPC falha temporariamente.
 */
import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useWlPermissions } from '@/contexts/WlPermissionsContext';
import { useAuth } from '@/hooks/useAuth';

interface Props { children: ReactNode }

export function WhiteLabelProtectedRoute({ children }: Props) {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { loading, hasAccess, error, refetch } = useWlPermissions();

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  // Falha técnica: não trata como "sem acesso" — evita redirecionamentos em cascata
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
          <h1 className="text-xl font-semibold">Falha ao carregar permissões</h1>
          <p className="text-sm text-muted-foreground">
            Não foi possível obter suas permissões do painel White Label.
            Tente novamente em alguns segundos.
          </p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!hasAccess) return <Navigate to="/access-denied" state={{ from: location.pathname }} replace />;

  return <>{children}</>;
}
